import { FC, useMemo, useState, useEffect } from "react"
import { useQueryClient } from "react-query"
import { Formik, Form, FormikValues } from "formik"
import * as Yup from 'yup'
import {     
  StatusErrorMessage,
  FormikNotesArea
} from "../../../../../components/widgets"

import { doFormSubmit, processNoteValues } from "../../../../../components/helpers"

import { pickBy, cloneDeep } from "lodash"
import { submitDLMPhase_3_1_2_3, submitDLMPhase_3_1_2_4, submitDLMPhase_3_1_5_1 } from "../../../../core/doclabel"
import { SubmitButtonRow, VerdictSelector } from "../../../../../components/layout"

const initPendingInspectionValues = {
  verdict: "",
  state: '',
  note: '',
  noteAttachments: [],
}

const schema = Yup.object({
  verdict: Yup.string().trim(),
  state: Yup.string().trim(),
  note: Yup.string().trim().label('Note')
  .when('state', {
    is: (val: string) => (val !== 'Applicant Confirmation'),
    then: (schema) => schema.required(),
  }),
  noteAttachments: Yup.array(),    
})


type Props = {
  id: number
  state: string
  appID: number
  doClose: Function
  hasVerdict?: boolean    
}

const SubmitNoteResponseForm: FC<Props> = ({id, state, appID, doClose, hasVerdict = false}) => {
  const queryClient = useQueryClient();
  const [ verdict, setVerdict ] = useState<string | null>(null);
  const [ initValues, setInitValues] = useState(initPendingInspectionValues);

  const callFunction = useMemo(()=> {
    if (! state) return undefined;
    switch(state) {
    case 'Site Labelling - Issue':
      return submitDLMPhase_3_1_2_3;            
    case 'Applicant Confirmation': 
      return submitDLMPhase_3_1_5_1;
    case 'Pending Re-Site Labelling':
      return submitDLMPhase_3_1_2_4;
    default: 
      return undefined;
    }
  }, [state]);


  const doSubmit = async (values: any, actions: FormikValues) => {        

    const { setStatus, setSubmitting } = actions;

    if (callFunction === undefined) {
      setStatus("Call undefined!");        
      setSubmitting(false);
      return;
    }

    const payload = { 
      ...pickBy(values, (value, key) => (value && (! ['verdict','notes','noteAttachments'].includes(key)))),
      siteApplicationid: id, 
    };

    processNoteValues(values, payload);

    if (values.verdict) {
      Object.assign(payload, { approved : values.verdict === 'approved'});
    }

    await doFormSubmit(callFunction, payload, setStatus, setSubmitting, () => {
      queryClient.invalidateQueries(['DLMAPPLICATION-LOAD', { dlmappid: appID } ]);
      doClose();                            
    });
  }

  useEffect(() => {
    if (! state) return;
    setInitValues({
      ...cloneDeep(initPendingInspectionValues), 
      state
    })
  }, [state])

  return(
  <Formik
    initialValues={initValues}
    validationSchema={schema}
    enableReinitialize={true}
    onSubmit={doSubmit}
  >
    {({status, setFieldValue, setStatus}) => (
    <Form id='kt_doclabel_submit_note_response_form' className='form' noValidate>
      <div
        className='d-flex flex-column px-2'
      >
      { hasVerdict && 
      <VerdictSelector
        verdict={verdict}
        setVerdict={setVerdict}
        setFieldValue={setFieldValue}
        setStatus={setStatus}
      />
      }

      {((! hasVerdict) || verdict) && 
      <>
      <FormikNotesArea required={state !== 'Applicant Confirmation'} />

      <StatusErrorMessage status={status} />
      <SubmitButtonRow doClose={doClose} />
      </>
      }
      </div>
    </Form>
    )}
  </Formik>
  )
}

export { SubmitNoteResponseForm }